import { Button, Flex, Modal, ModalProps, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useInvestigationsServiceGetInvestigationTestEndpoint } from 'src/api/queries'
import { InvestigationTestResultDto } from 'src/api/requests'
import { VALIDATE_RESULT_PERMISSIONS } from 'src/app-constants'
import { ApproveTestModal } from 'src/components'
import { useUser } from 'src/hooks'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

type Props = {
  testId?: string
  onCancel: () => void
  onSuccess?: () => void
} & ModalProps

export const ResultModal = ({ onCancel, testId, ...props }: Props) => {
  const { t, currentLang } = useLocales()

  const { hasPermission } = useUser()

  const [isOpenApproveModal, setIsOpenApproveModal] = useState(false)

  const { data, isLoading } =
    useInvestigationsServiceGetInvestigationTestEndpoint(
      {
        id: String(testId)
      },
      [testId],
      { enabled: !!testId }
    )

  const hasApprovePermission = hasPermission(VALIDATE_RESULT_PERMISSIONS)

  const columns: ColumnsType<InvestigationTestResultDto> = [
    {
      title: t('laboratory_classifiers.investigation_infection'),
      key: 'infection',
      render: (_, { testType }) => testType?.translation?.[currentLang?.value]
    },
    {
      title: t('tests_results.result'),
      key: 'result',
      render: (_, { resultOption }) =>
        resultOption?.translation?.[currentLang?.value]
    },
    {
      key: 'result value',
      title: t('investigations.result_value'),
      render: (_, { resultValueOption, laboratoryResultStatus }) =>
        laboratoryResultStatus ||
        resultValueOption?.translation?.[currentLang?.value]
    },
    {
      key: 'result numeric value',
      title: t('investigations.result_numeric_value'),
      render: (_, { resultValue }) => resultValue
    },
    {
      key: 'result date',
      title: t('tests_results.result_date'),
      render: (_, { resultDate }) =>
        dayjs.utc(resultDate).tz().format(dayMonthYearHourMinutesFormatWithDot)
    },
    {
      key: 'validated by',
      title: t('tests_results.validated_by'),
      render: (_, { validatedByUser }) =>
        validatedByUser?.firstName
          ? `${validatedByUser?.firstName} ${validatedByUser?.lastName}`
          : '-'
    },
    {
      key: 'created by',
      title: t('tests_results.created_by'),
      render: (_, { createdByUser }) =>
        createdByUser?.firstName
          ? `${createdByUser?.firstName} ${createdByUser?.lastName}`
          : '-'
    }
  ]

  const onApproved = () => {
    props?.onSuccess?.()
    onCancel()
  }

  return (
    <Modal
      open
      title={t('investigations.view_results')}
      onCancel={onCancel}
      width={1000}
      footer={null}
      maskClosable={false}
      loading={isLoading}
      {...props}
    >
      <Flex vertical gap={24}>
        <Table
          pagination={false}
          dataSource={data?.results}
          rowKey={(record) => JSON.stringify(record)}
          columns={columns}
          scroll={{ x: 'max-content' }}
        />

        <Flex justify="center" gap={8}>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          {hasApprovePermission && !data?.isApproved && (
            <Button type="primary" onClick={() => setIsOpenApproveModal(true)}>
              {t('analysis.add_analysis.send_test_result')}
            </Button>
          )}
        </Flex>
      </Flex>

      {isOpenApproveModal && (
        <ApproveTestModal
          testId={String(testId)}
          results={data?.results}
          onCancel={() => setIsOpenApproveModal(false)}
          onSuccess={onApproved}
        />
      )}
    </Modal>
  )
}
